import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  VStack,
  HStack,
  Box,
  Text,
  Input,
  IconButton,
  Button,
  Badge,
  Select,
  Spinner,
  useColorModeValue,
  useToast,
  Divider,
  Tag,
  Flex,
  Tooltip,
} from '@chakra-ui/react';
import { FiSend, FiTrash2, FiCpu, FiCheckCircle, FiCopy, FiRefreshCw } from 'react-icons/fi';
import { querySoupModel, ChatMessage } from '../../services/soupAiClient';

interface SovereignAiDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
  userName?: string;
}

const PRESET_PROMPTS = [
  {
    label: '🛡️ ZATCA Phase-2 Audit',
    prompt: 'Review the following invoice description against KSA ZATCA Phase-2 requirements (bilingual Arabic/English, 15% VAT breakdown, mandatory fields): ',
  },
  {
    label: '📄 Draft Commercial Proposal',
    prompt: 'Draft an executive commercial proposal introduction for Elite Success Trading Est. offering industrial insulation & contracting supplies to a KSA Tier-1 client.',
  },
  {
    label: '🔍 Technical Specification Check',
    prompt: 'Explain the technical compliance requirements of ASTM C612 mineral wool board insulation for industrial plant applications in Saudi Arabia.',
  },
];

export const SovereignAiDrawer: React.FC<SovereignAiDrawerProps> = ({
  isOpen,
  onClose,
  userName,
}) => {
  const [selectedModel, setSelectedModel] = useState<string>('soup-trading-erp:latest');
  const [inputMessage, setInputMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: `مرحباً ${userName || 'بك'}! أنا نموذج الذكاء الاصطناعي السيادي (Sovereign AI) الخاص بمجموعة إيليت (Elite Success). تم تدريبي وتشغيلي محلياً على سيرفر ديل (Dell Server) لضمان الخصوصية التامة وسيادة البيانات وفق المعايير السعودية.\n\nكيف يمكنني مساعدتك اليوم في عروض الأسعار، مراجعة متطلبات هيئة الزكاة والضريبة (ZATCA)، أو التدقيق الفني للمنتجات؟`,
    },
  ]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const bgDrawer = useColorModeValue('white', 'gray.800');
  const userBubbleBg = useColorModeValue('blue.50', 'blue.900');
  const assistantBubbleBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSendMessage = async (textToSend?: string) => {
    const text = (textToSend || inputMessage).trim();
    if (!text || isLoading) return;

    const newMessages: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInputMessage('');
    setIsLoading(true);

    try {
      // In web browser, use the same-origin reverse-proxy route /api/ai/v1
      const isBrowser = typeof window !== 'undefined';
      const endpoint = isBrowser ? `${window.location.origin}/api/ai/v1` : 'http://100.105.50.23:11434/v1';

      const responseText = await querySoupModel(newMessages, {
        model: selectedModel,
        baseUrl: endpoint,
        temperature: 0.3,
        maxTokens: 600,
      });

      setMessages((prev) => [...prev, { role: 'assistant', content: responseText }]);
    } catch (err: any) {
      console.error('Sovereign AI Error:', err);
      toast({
        title: 'خطأ في الاتصال بالنموذج السيادي',
        description: err.message || 'تعذر الوصول إلى سيرفر الذكاء الاصطناعي المحلي.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: '⚠️ تعذر الاتصال بمحرك الذكاء الاصطناعي المحلي. يرجى التأكد من تشغيل نفق WireGuard أو إعادة المحاولة.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'تم النسخ',
      status: 'success',
      duration: 1500,
      isClosable: true,
    });
  };

  const handleClear = () => {
    setMessages([
      {
        role: 'assistant',
        content: 'تم بدء محادثة جديدة. يمكنك سؤالي عن العمليات التجارية أو اختيار أحد القوالب الجاهزة أدناه.',
      },
    ]);
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent bg={bgDrawer}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={borderColor} pb={3}>
          <HStack justify="space-between" pr={6}>
            <HStack spacing={2}>
              <FiCpu color="#3182ce" size={20} />
              <VStack align="flex-start" spacing={0}>
                <HStack spacing={2}>
                  <Text fontSize="md" fontWeight="bold">
                    Sovereign AI Hub
                  </Text>
                  <Tag size="sm" colorScheme="green" borderRadius="full">
                    <HStack spacing={1}>
                      <FiCheckCircle size={10} />
                      <Text fontSize="2xs">Dell Ground Server</Text>
                    </HStack>
                  </Tag>
                </HStack>
                <Text fontSize="2xs" color="gray.500">
                  KSA Sovereign Local Model • ZATCA & Technical Engine
                </Text>
              </VStack>
            </HStack>
            <Tooltip label="بدء محادثة جديدة">
              <IconButton
                aria-label="Clear chat"
                icon={<FiTrash2 />}
                size="xs"
                variant="ghost"
                onClick={handleClear}
              />
            </Tooltip>
          </HStack>

          <HStack mt={3} spacing={2}>
            <Text fontSize="xs" fontWeight="medium" color="gray.600">
              Model:
            </Text>
            <Select
              size="xs"
              borderRadius="md"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              maxW="240px"
            >
              <option value="soup-trading-erp:latest">soup-trading-erp (Fine-tuned)</option>
              <option value="qwen2.5:3b">qwen2.5:3b (General Reasoning)</option>
            </Select>
          </HStack>
        </DrawerHeader>

        <DrawerBody p={4} display="flex" flexDirection="column">
          {/* Quick Preset Buttons */}
          <Box mb={3}>
            <Text fontSize="2xs" fontWeight="bold" color="gray.400" mb={1} textTransform="uppercase">
              Quick Actions
            </Text>
            <Flex wrap="wrap" gap={1.5}>
              {PRESET_PROMPTS.map((item, idx) => (
                <Button
                  key={idx}
                  size="xs"
                  variant="outline"
                  borderRadius="full"
                  fontSize="2xs"
                  onClick={() => {
                    setInputMessage(item.prompt);
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Flex>
          </Box>

          <Divider mb={3} />

          {/* Messages Container */}
          <VStack spacing={3} align="stretch" flex={1} overflowY="auto" pr={1}>
            {messages.map((msg, index) => {
              const isAssistant = msg.role === 'assistant';
              return (
                <Box
                  key={index}
                  alignSelf={isAssistant ? 'flex-start' : 'flex-end'}
                  maxW="88%"
                  bg={isAssistant ? assistantBubbleBg : userBubbleBg}
                  p={3}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                  boxShadow="sm"
                >
                  <HStack justify="space-between" mb={1}>
                    <Badge size="xs" colorScheme={isAssistant ? 'purple' : 'blue'}>
                      {isAssistant ? (selectedModel.includes('soup') ? 'Soup Domain Model' : 'Qwen Assistant') : 'You'}
                    </Badge>
                    {isAssistant && (
                      <IconButton
                        aria-label="Copy"
                        icon={<FiCopy size={10} />}
                        size="2xs"
                        variant="ghost"
                        onClick={() => handleCopy(msg.content)}
                      />
                    )}
                  </HStack>
                  <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="tall">
                    {msg.content}
                  </Text>
                </Box>
              );
            })}
            {isLoading && (
              <Box alignSelf="flex-start" bg={assistantBubbleBg} p={3} borderRadius="lg">
                <HStack spacing={2}>
                  <Spinner size="xs" color="blue.500" />
                  <Text fontSize="xs" color="gray.500">
                    جاري التفكير والتوليد عبر سيرفر ديل...
                  </Text>
                </HStack>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </VStack>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor={borderColor} p={3}>
          <HStack w="full" spacing={2}>
            <Input
              placeholder="اكتب استفسارك التجاري أو التقني..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              size="sm"
              borderRadius="lg"
              autoFocus
            />
            <IconButton
              aria-label="Send message"
              icon={<FiSend />}
              colorScheme="blue"
              size="sm"
              isLoading={isLoading}
              onClick={() => handleSendMessage()}
              borderRadius="lg"
            />
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
